import { Injectable } from '@angular/core';

/**
 * IndexedDBService
 *
 * Provides structured offline storage using IndexedDB.
 * This is infrastructure for future offline support - not fully utilized yet.
 *
 * Features:
 * - Structured storage with object stores
 * - Indexed queries for fast lookups
 * - Bulk operations for performance
 * - Promise-based API for async operations
 *
 * Object Stores:
 * - patients: Patient records with profileId, practiceId indexes
 * - appointments: Appointments with patientId, date, practiceId indexes
 * - drafts: Drafts with patientId, createdDate indexes
 * - reports: Reports with patientId, createdDate indexes
 * - sync_queue: Pending sync operations (for future offline sync)
 * - metadata: Key-value store for sync timestamps and metadata
 */
@Injectable({
  providedIn: 'root'
})
export class IndexedDBService {
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'xmedical';
  private readonly DB_VERSION = 3;
  private initPromise: Promise<void>;

  constructor() {
    this.initPromise = this.initDB();
  }

  /**
   * Initialize IndexedDB with schema
   */
  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('[IndexedDB] Failed to open database', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onblocked = () => {
        console.warn('[IndexedDB] Database upgrade blocked. Close all other tabs and reload.');
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;
        const newVersion = event.newVersion || this.DB_VERSION;
        console.log(`[IndexedDB] Upgrading from v${oldVersion} to v${newVersion}`);

        // Create patients store
        if (!db.objectStoreNames.contains('patients')) {
          const patients = db.createObjectStore('patients', { keyPath: 'id' });
          patients.createIndex('profileId', 'profileId', { unique: false });
          patients.createIndex('practiceId', 'practiceId', { unique: false });
          patients.createIndex('modifiedDate', 'modifiedDate', { unique: false });
        }

        // Create appointments store
        if (!db.objectStoreNames.contains('appointments')) {
          const appointments = db.createObjectStore('appointments', { keyPath: 'id' });
          appointments.createIndex('patientId', 'patientId', { unique: false });
          appointments.createIndex('date', 'date', { unique: false });
          appointments.createIndex('practiceId', 'practiceId', { unique: false });
        }

        // Create drafts store
        if (!db.objectStoreNames.contains('drafts')) {
          const drafts = db.createObjectStore('drafts', { keyPath: 'id' });
          drafts.createIndex('patientId', 'patientId', { unique: false });
          drafts.createIndex('createdDate', 'createdDate', { unique: false });
        }

        // Create reports store
        if (!db.objectStoreNames.contains('reports')) {
          const reports = db.createObjectStore('reports', { keyPath: 'id' });
          reports.createIndex('patientId', 'patientId', { unique: false });
          reports.createIndex('createdDate', 'createdDate', { unique: false });
        }

        // Create sync queue store for pending operations
        if (!db.objectStoreNames.contains('sync_queue')) {
          const syncQueue = db.createObjectStore('sync_queue', {
            keyPath: 'id',
            autoIncrement: true
          });
          syncQueue.createIndex('timestamp', 'timestamp', { unique: false });
          syncQueue.createIndex('status', 'status', { unique: false });
        }

        // Create metadata store for sync timestamps, etc.
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }

        // Create dashboard_data store for large query results (flagged patients, today's appointments)
        if (!db.objectStoreNames.contains('dashboard_data')) {
          const dashboardData = db.createObjectStore('dashboard_data', { keyPath: 'key' });
          dashboardData.createIndex('userId', 'userId', { unique: false });
          dashboardData.createIndex('practiceId', 'practiceId', { unique: false });
          dashboardData.createIndex('dataType', 'dataType', { unique: false });
          dashboardData.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.db) {
      await this.initPromise;
    }
    if (!this.db) {
      throw new Error('[IndexedDB] Database not initialized');
    }
  }

  /**
   * Get all items from a store
   *
   * @param storeName - Object store name
   * @returns Promise with array of all items
   */
  async getAll<T>(storeName: string): Promise<T[]> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a single item by ID
   *
   * @param storeName - Object store name
   * @param id - Item ID
   * @returns Promise with item or undefined if not found
   */
  async get<T>(storeName: string, id: any): Promise<T | undefined> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Add a new item to a store
   *
   * @param storeName - Object store name
   * @param item - Item to add
   * @returns Promise with the added item
   */
  async add<T>(storeName: string, item: T): Promise<T> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.add(item);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(item);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update an existing item
   *
   * @param storeName - Object store name
   * @param id - Item ID
   * @param updates - Partial updates to apply
   * @returns Promise with updated item
   */
  async update<T>(storeName: string, id: any, updates: Partial<T>): Promise<T> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const getRequest = store.get(id);

    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (!item) {
          reject(new Error(`[IndexedDB] Item with id ${id} not found in ${storeName}`));
          return;
        }

        const updated = { ...item, ...updates };
        const putRequest = store.put(updated);

        putRequest.onsuccess = () => resolve(updated);
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete an item
   *
   * @param storeName - Object store name
   * @param id - Item ID
   * @returns Promise that resolves when delete is complete
   */
  async delete(storeName: string, id: any): Promise<void> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(id);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Bulk add items (uses put to handle updates)
   *
   * @param storeName - Object store name
   * @param items - Array of items to add/update
   * @returns Promise that resolves when all items are added
   */
  async bulkAdd<T>(storeName: string, items: T[]): Promise<void> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);

    for (const item of items) {
      store.put(item); // put instead of add to handle updates
    }

    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Clear all items from a store
   *
   * @param storeName - Object store name
   * @returns Promise that resolves when store is cleared
   */
  async clear(storeName: string): Promise<void> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Query items by index
   *
   * @param storeName - Object store name
   * @param indexName - Index name
   * @param value - Value to query
   * @returns Promise with array of matching items
   */
  async queryByIndex<T>(
    storeName: string,
    indexName: string,
    value: any
  ): Promise<T[]> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Count items in a store
   *
   * @param storeName - Object store name
   * @returns Promise with count
   */
  async count(storeName: string): Promise<number> {
    await this.ensureInitialized();
    const tx = this.db!.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.count();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Check if IndexedDB is supported and available
   *
   * @returns True if IndexedDB is supported
   */
  static isSupported(): boolean {
    return 'indexedDB' in window;
  }
}
